/**
 * AIDashboard — hub principal IA pour Sphere
 * Mise en page aérée avec hiérarchie visuelle claire
 */

import {
  MachineLearning,
  Edit,
  Document,
  Pen,
  Chat,
  Catalog,
  Time,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  TaskComplete,
} from '@carbon/icons-react';
import { Stack, Grid, Column, Layer, InlineLoading } from '@carbon/react';
import { useState } from 'react';
import { useAIHealth } from '@sphere/learning/data-access';
import {
  AIStatCard,
  HowItWorksSection,
  AiDashboardCard,
} from '@sphere/learning/ui';
import styles from './AIDashboard.module.scss';

// ------------------------------
// Configuration
// ------------------------------

const AI_FEATURES = [
  {
    id: 'quiz',
    title: 'Quiz à Choix Multiples',
    description:
      'Générez des quiz interactifs avec des questions et réponses générées par IA.',
    icon: MachineLearning,
    color: 'purple',
    path: '/ai/quiz',
    estimatedTime: '2–3 min',
  },
  {
    id: 'cloze-quiz',
    title: 'Quiz à Trous',
    description:
      'Créez des exercices à trous pour tester la mémoire et la compréhension.',
    icon: Edit,
    color: 'green',
    path: '/ai/cloze-quiz',
    estimatedTime: '2–3 min',
  },
  {
    id: 'summary',
    title: 'Résumé Intelligent',
    description:
      'Transformez vos longs documents en résumés concis avec les points clés.',
    icon: Document,
    color: 'blue',
    path: '/ai/summary',
    estimatedTime: '1–2 min',
  },
  {
    id: 'exercises',
    title: 'Exercices PDF',
    description:
      'Générez automatiquement des exercices imprimables à partir de vos documents.',
    icon: Pen,
    color: 'orange',
    path: '/ai/exercises',
    estimatedTime: '3–4 min',
  },
  {
    id: 'qa',
    title: 'Fiches Q&R',
    description:
      'Générez des fiches questions/réponses pour la révision et la compréhension.',
    icon: Chat,
    color: 'indigo',
    path: '/ai/qa',
    estimatedTime: '3–4 min',
  },
  {
    id: 'grade-copy',
    title: 'Correction de Copie',
    description:
      "Uploadez un corrigé type et la copie d'un élève pour obtenir une note détaillée générée par l'IA.",
    icon: TaskComplete,
    color: 'green',
    path: '/ai/grade-copy',
    estimatedTime: '2–4 min',
  },
] as const;

// ------------------------------
// Main Component
// ------------------------------

export default function AIDashboard() {
  const { data: healthData, isLoading } = useAIHealth();
  const available = !!healthData?.available;
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  return (
    <div className={styles.dashboard}>
      <div className={styles.pageWrapper}>
        {/* Hero + Stats fusionnés */}
        <section className={styles.heroSection}>
          <Layer className={styles.heroLayer}>
            <div className={styles.heroContent}>
              <div className={styles.heroIcon}>
                <MachineLearning size={32} />
              </div>
              <div className={styles.heroText}>
                <h1 className={styles.heroTitle}>Sphere AI</h1>
                <p className={styles.heroSubtitle}>
                  Intelligence artificielle pour l'apprentissage et
                  l'enseignement
                </p>
              </div>
              <div className={styles.statusBadge}>
                {isLoading ? (
                  <InlineLoading
                    description="Vérification..."
                    status="active"
                  />
                ) : (
                  <span className={styles.statusWrapper}>
                    <span
                      className={`${styles.statusDot} ${
                        available ? styles.statusOnline : styles.statusOffline
                      }`}
                    />
                    {available ? 'En ligne' : 'Hors ligne'}
                  </span>
                )}
              </div>
            </div>
            <p className={styles.heroTagline}>
              Transformez vos documents en contenu éducatif interactif — quiz,
              résumés, exercices.
            </p>
            <div className={styles.statsBar}>
              <div className={styles.statItem}>
                <AIStatCard icon={Catalog} title="Outils IA" value="5" />
              </div>
              <div className={styles.statItem}>
                <AIStatCard
                  icon={Time}
                  title="Temps de génération"
                  value="1–4 min"
                />
              </div>
              <div className={styles.statItem}>
                <AIStatCard icon={Document} title="Fichier max." value="50MB" />
              </div>
            </div>
            <div className={styles.howBlock}>
              <button
                type="button"
                className={styles.howSectionToggle}
                onClick={() => setHowItWorksOpen(!howItWorksOpen)}
                aria-expanded={howItWorksOpen}
              >
                <span>Comment ça fonctionne ?</span>
                {howItWorksOpen ? (
                  <ChevronUp size={20} className={styles.toggleIcon} />
                ) : (
                  <ChevronDown size={20} className={styles.toggleIcon} />
                )}
              </button>
              {howItWorksOpen && (
                <div className={styles.howSectionContent}>
                  <HowItWorksSection />
                </div>
              )}
            </div>
          </Layer>
        </section>

        {/* Outils — 3 colonnes sur grand écran */}
        <section className={styles.toolsSection}>
          <h2 className={styles.toolsTitle}>Choisissez un outil</h2>
          <p className={styles.toolsSubtitle}>
            Sélectionnez l'outil adapté à votre besoin pédagogique
          </p>
          <Grid narrow className={styles.toolsGrid}>
            {AI_FEATURES.map((feature) => (
              <Column
                key={feature.id}
                lg={5}
                md={4}
                sm={4}
                className={styles.toolColumn}
              >
                <AiDashboardCard {...feature} available={available} />
              </Column>
            ))}
          </Grid>
        </section>
      </div>
    </div>
  );
}
